/**
 * --validate command: verify config, auth, and connectivity in one shot.
 *
 * Steps:
 * 1. Load and validate config file
 * 2. Authenticate (silent token acquisition)
 * 3. Call list_environments to verify API connectivity
 * 4. Verify secondary API consent needed by non-core tool surfaces
 * 5. Report results and exit
 */

import { loadConfig, resolveEnvironment, getConfigPath } from "../config/loader.js";
import { createAuthProviderFromConfig } from "../auth/wam-broker.js";
import { FlowManagementApi } from "../api/flow-management.js";
import { ConfigurationError } from "../utils/errors.js";
import { checkSecondaryResourceAuth } from "./secondary-resources.js";
import { formatFeatureList, normalizeFeatures } from "../config/features.js";

export async function runValidate(): Promise<void> {
  console.error("Validating Power Automate MCP configuration...\n");

  // Step 1: Config
  let config;
  try {
    const configPath = getConfigPath();
    console.error(`  [1/4] Config file: ${configPath}`);
    config = loadConfig();
    const envNames = Object.keys(config.environments).filter(k => k !== "default");
    const enabledFeatures = normalizeFeatures(config.features?.enabled);
    console.error(`        Client ID: ${config.auth.clientId}`);
    console.error(`        Tenant: ${config.auth.tenantId ?? "common"}`);
    console.error(`        Features: ${formatFeatureList(enabledFeatures)}`);
    console.error(`        Environments: ${envNames.join(", ") || "(none)"}`);
    console.error(`        Default: ${config.environments.default}`);
    console.error("        ✓ Config valid\n");
  } catch (err) {
    const msg = err instanceof ConfigurationError ? err.message : String(err);
    console.error(`        ✗ Config error: ${msg}\n`);
    process.exit(1);
  }

  // Step 2: Auth
  let authProvider;
  try {
    console.error("  [2/4] Authenticating...");
    authProvider = await createAuthProviderFromConfig(
      config.auth.clientId,
      config.auth.tenantId,
      undefined,
      true // silentOnly
    );
    const token = await authProvider.getToken();
    console.error(`        Token acquired (${token.length} chars)`);
    const account = authProvider.getCurrentAccount();
    if (account) {
      console.error(`        Account: ${account.username ?? account.name ?? "unknown"}`);
    }
    console.error("        ✓ Auth valid\n");
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error(`        ✗ Auth error: ${msg}`);
    console.error("        Run: powerautomate-mcp --setup\n");
    process.exit(1);
  }

  // Step 3: Connectivity
  try {
    console.error("  [3/4] Testing API connectivity...");
    const defaultEnv = resolveEnvironment(config);
    const flowApi = new FlowManagementApi({
      authProvider,
      defaultEnvironmentId: defaultEnv.id,
    });
    const result = await flowApi.listFlows({ environment: defaultEnv.id, top: 1 });
    const count = result.flows.length;
    console.error(`        Environment: ${defaultEnv.name} (${defaultEnv.id})`);
    console.error(`        Flow API reachable (${count >= 1 ? "flows found" : "connected"})`);
    console.error("        ✓ Connectivity valid\n");
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error(`        ✗ Connectivity error: ${msg}\n`);
    process.exit(1);
  }

  // Step 4: Secondary resource authorization
  try {
    console.error("  [4/4] Testing secondary API authorization...");
    const results = await checkSecondaryResourceAuth(authProvider, config.features.enabled);
    const missingCritical = results.filter((r) => !r.ok && r.critical);
    if (results.length === 0) {
      console.error("        ✓ No secondary API permissions selected\n");
    } else {
      for (const result of results) {
        if (result.ok) {
          console.error(`        ✓ ${result.label} authorized`);
        } else {
          const icon = result.critical ? "✗" : "⚠";
          console.error(`        ${icon} ${result.label} not authorized`);
          if (result.message) {
            console.error(`          ${result.message}`);
          }
          console.error(`          ${result.nextStep}`);
        }
      }
    }
    if (missingCritical.length > 0) {
      console.error("\nCritical API permissions are missing. Add them in Entra ID, grant admin consent, then re-run setup.\n");
      process.exit(1);
    }
    if (results.length > 0) {
      console.error("        ✓ Secondary API authorization valid\n");
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error(`        ✗ Secondary API authorization check failed: ${msg}\n`);
    process.exit(1);
  }

  console.error("All checks passed. Server is ready to run.\n");
  process.exit(0);
}
